import { ChatBubbleOvalLeftEllipsisIcon, DocumentPlusIcon } from "@heroicons/react/24/outline";
import { ChatBubbleOvalLeftIcon, DocumentMinusIcon } from "@heroicons/react/24/solid";
import React from "react";
import { formatJumpHeightM, getRotationLabels } from "../utils/Constants";
import { DVEventString } from "../utils/DVWFile";
import { getEventInfo, getEventInfoBadge, getEventStringColor, getEventStringColorBadge } from "../utils/Utils";

function EventItem({ event, isSelected, onEventSelected, isFirst = false, innerRef }) {
  const [, forceUpdate] = React.useState(0);
  const [showCommentModal, setShowCommentModal] = React.useState(false);
  const [commentText, setCommentText] = React.useState("");

  const isHomePlayer = (e) => {
    if (!e.Player) return false;
    if (e.dvString !== undefined) return e.dvString.substring(0, 1) === "*";
    return e.homePlayer;
  };

  const home = isHomePlayer(event);

  const togglePlaylist = (e) => {
    e.stopPropagation();
    event.playlist = !event.playlist;
    forceUpdate((n) => n + 1);
  };

  const openCommentModal = (e) => {
    e.stopPropagation();
    setCommentText(event.comment || "");
    setShowCommentModal(true);
  };

  const saveComment = () => {
    if (!event.playlist) event.playlist = true;
    event.comment = commentText;
    setShowCommentModal(false);
    forceUpdate((n) => n + 1);
  };

  const getLandingImpact = (ev) => {
    if (ev.vertData?.landingpeakgs !== undefined) {
      const v = ev.vertData.landingpeakgs;
      const cn =
        v < 10 ? "badge-success" : v < 20 ? "badge-warning" : "badge-error";
      return { value: v, cn };
    }
    return null;
  };

  const eventLabel =
    event.DVGrade === undefined
      ? getEventInfo(event).sub
      : DVEventString(event);

  const eventColorCls =
    event.DVGrade === undefined
      ? getEventInfo(event).subcolor
      : isSelected
        ? ""
        : getEventStringColor(event);
  // : "font-mono " + (isSelected ? "" : getEventStringColor(event));

    const eventColorCls2 =
    event.DVGrade === undefined
      ? getEventInfoBadge(event).subcolor
      : getEventStringColorBadge(event);

  // First event in a rally has no left border (the rally row already carries it).
  const borderCls = isFirst
    ? ""
    : isSelected
      ? "border-l-4 border-l-accent-content"
      : home
        ? "border-l-4 border-l-primary"
        : "border-l-4 border-l-secondary";

  const bgCls = isSelected
    ? "bg-accent bg-opacity-30 text-accent-content"
    : event.powerPlay
      ? "bg-warning/10 hover:bg-warning/20 text-base-content"
      : "bg-base-100 hover:bg-base-200 text-base-content";

  const impact = event.vertData ? getLandingImpact(event) : null;

  return (
    <>
    <div
      ref={innerRef}
      className={`px-2 pr-4 py-1 cursor-pointer transition-colors ${borderCls} ${bgCls}`}
      onClick={() => onEventSelected(event)}
    >
      {/* Row 1: dot · player name · optional badges · rotation · playlist */}
      <div className="flex items-center gap-1.5">
        <span
          className={`w-2 h-2 rounded-full flex-shrink-0 ${
            isSelected
              ? "bg-accent-content"
              : home
                ? "bg-primary"
                : "bg-secondary"
          }`}
        />

        <span className="flex-1 text-xs font-semibold truncate">
          {event.Player
            ? `${event.Player.shirtNumber !== "-1" ? event.Player.shirtNumber + ". " : ""}${event.Player.NickName.toUpperCase()}`
            : ""}
        </span>

        {event.radar != null && (
          <span className="badge badge-info badge-xs">{event.radar.speed}</span>
        )}
        {event.vertData && (
          <span className="badge badge-info badge-xs">
            {event.vertData.vertinches.toFixed(1)}"
          </span>
        )}
        {event.imaHeightM != null && (
          <span className="badge badge-info badge-xs" title="Jump height">
            {formatJumpHeightM(event.imaHeightM)}
          </span>
        )}
        {event.imaDurationSec != null && (
          <span className="badge badge-ghost badge-xs">
            {event.imaDurationSec.toFixed(2)}s
          </span>
        )}
        {impact && (
          <span className={`badge badge-xs ${impact.cn}`}>{impact.value}</span>
        )}
        {event.powerPlay && (
          <span
            className="badge badge-warning badge-xs gap-0.5 flex-shrink-0"
            title="Power Play"
          >
            ⚡{event.powerPlay}
          </span>
        )}

        {/* Rotation only — score removed */}
        <span
          className={`text-xs tabular-nums whitespace-nowrap ${
            isSelected ? "opacity-80" : "opacity-40"
          }`}
        >
          {event.Row > 0 ? getRotationLabels()[event.Row - 1] : `R${event.Row}`}
        </span>

        <span
          className="tooltip tooltip-left flex-shrink-0"
          data-tip={event.comment ? "Edit note" : "Add note"}
          onClick={openCommentModal}
        >
          {event.comment ? (
            <ChatBubbleOvalLeftIcon className="w-4 h-4 text-info" />
          ) : (
            <ChatBubbleOvalLeftEllipsisIcon
              className={`w-4 h-4 ${isSelected ? "opacity-70" : "opacity-25 hover:opacity-70"}`}
            />
          )}
        </span>

        <span
          className="tooltip tooltip-left flex-shrink-0"
          data-tip={event.playlist ? "Remove from playlist" : "Add to playlist"}
          onClick={togglePlaylist}
        >
          {event.playlist ? (
            <DocumentMinusIcon className="w-4 h-4 text-warning" />
          ) : (
            <DocumentPlusIcon
              className={`w-4 h-4 ${
                isSelected ? "opacity-70" : "opacity-25 hover:opacity-70"
              }`}
            />
          )}
        </span>
      </div>

      {/* Row 2: skill / event string */}
      <div className="-mt-0.5 ml-3.5">
        <span className={`py-0.5 px-2 badge-xs bg-opacity-60 rounded-md ${eventColorCls2}`}>{eventLabel}</span>

        {/* <span className={`text-xs ${eventColorCls}`}>{eventLabel}</span> */}
      </div>
    </div>

    {showCommentModal && (
      <div
        className="modal modal-open z-[9999]"
        onClick={(e) => { e.stopPropagation(); setShowCommentModal(false); }}
      >
        <div className="modal-box max-w-sm" onClick={(e) => e.stopPropagation()}>
          <h3 className="font-semibold text-base mb-3">Playlist note</h3>
          <textarea
            className="textarea textarea-bordered w-full resize-none text-sm"
            placeholder="Add a note to this clip…"
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            rows={3}
            autoFocus
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === "Escape") setShowCommentModal(false);
            }}
          />
          <div className="modal-action">
            <button
              className="btn btn-ghost btn-sm"
              onClick={(e) => { e.stopPropagation(); setShowCommentModal(false); }}
            >
              Cancel
            </button>
            <button
              className="btn btn-primary btn-sm"
              onClick={(e) => { e.stopPropagation(); saveComment(); }}
            >
              Save
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
}

export default EventItem;
